import React, { useEffect, useMemo } from 'react';
import { FAV_CONSTANTS } from '@constants/favorites';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import DefaultLayout from '@layouts/default-layout';
import { UpdateDataLayerComponent } from '@context/analytics/analytics.context';
import PageAuthenticationGuard from '@components/atoms/authentication/page-authentication-guard/page-authentication-guard';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import extractFromContext from '@helpers/utils/extract-from-context';
import useUser from '@hooks/user/use-user';
import FavoritesFeedHotFilters from '@components/organisms/favorites/v2/favorites-feed-hot-filters/favorites-feed-hot-filters';
import { NextPageWithLayout } from 'next';
import { updateParamsInUrl } from '@helpers/routing';
import { HotFiltersProvider } from '@context/hot-filters.context';
import { convertAppliedHotFiltersToPayload } from '@helpers/utils/hot-filters/hot-filters-parsing-util';
import { getAppInitialProps } from '@helpers/initial-props';
import { FAV_DEFAULT_SORT_ORDER } from '@constants/profile-fav-sort';
import { getValidFavSortOrder } from '@helpers/utils/profile-fav-sort/get-valid-sort-order';

const FavoritesPage: NextPageWithLayout = (): React.JSX.Element => {
  const { isAuthenticated } = useUser();
  const router = useRouter();
  const { t } = useTranslation();

  const { sortBy, favoritesUserSlug, ...filters } = router?.query as {
    sortBy: string;
    favoritesUserSlug: string;
  };

  // With Hotfilters Data layer for analytics
  const dataLayerHotFilters = useMemo(() => {
    return isAuthenticated
      ? {
          ...FAV_CONSTANTS.FAV_SCREEN_CONTEXT,
          filters_details: JSON.stringify(convertAppliedHotFiltersToPayload(filters)),
          filters_applied: '{}',
          sorting: sortBy,
        }
      : {};
  }, [isAuthenticated]);

  useEffect(() => {
    if (!router?.asPath.includes('sortBy')) {
      updateParamsInUrl({ order: '' });
      updateParamsInUrl({ sortBy: FAV_DEFAULT_SORT_ORDER });
    }
  }, []);

  return (
    <PageAuthenticationGuard
      modalType="login"
      redirectionUrl={t('LINKS.NEW_IN')}
    >
      <HotFiltersProvider>
        <UpdateDataLayerComponent data={dataLayerHotFilters} />
        <FavoritesFeedHotFilters
          pageType="favourite"
          hasHeader={true}
          sortOrder={getValidFavSortOrder(sortBy)}
        />
      </HotFiltersProvider>
    </PageAuthenticationGuard>
  );
};

FavoritesPage.Layout = [DefaultLayout];
FavoritesPage.PageType = 'FavoritesPage';

export async function getServerSideProps(context) {
  const { locale } = extractFromContext(context);
  const initialProps = await getAppInitialProps(context);
  const translations = await serverSideTranslations(locale, ['default-layout', 'vc-authentication', 'a11y', 'common']);
  return {
    props: {
      ...initialProps,
      ...translations,
    },
  };
}

export default FavoritesPage;
